/**
 * useCalendarNavigation Hook
 * Manages calendar navigation, date state, and URL synchronization
 */

import { useState, useCallback, useEffect } from 'react';
import { push } from 'react-router-redux';
import moment from 'moment';
import { parseStartDateFromParams, isCurrentOrPastMonth, getMonthRange } from '../utils/dateUtils';
import { buildCalendarUrl } from '../utils/urlUtils';

/**
 * Custom hook for managing calendar navigation
 * @param {Object} match - React Router match object
 * @param {Object} user - Current user object
 * @param {Function} dispatch - Redux dispatch function
 * @param {Function} onNavigateCallback - Callback to trigger data fetch on navigation
 * @returns {Object} { currentDate, navigateToDate, handleMonthChange }
 */
export const useCalendarNavigation = (match, user, dispatch, saved, onNavigateCallback) => {

  // Initialize date from URL params or default to current month
  const [currentDate, setCurrentDate] = useState(() =>
    parseStartDateFromParams(match.params)
  );
  // Update current date when URL params change
  useEffect(() => {
    const dateFromParams = parseStartDateFromParams(match.params);
    if (dateFromParams !== currentDate) {
      setCurrentDate(dateFromParams);
    }
  }, [match.params.month, match.params.year]);

  useEffect(() => {
    navigateToDate(parseStartDateFromParams(match.params), 'doctorChange')
  }, [saved])

  /**
   * Navigate to a specific date
   * @param {Date|string} selectedDate - Date to navigate to
   * @param {string} view - View type (e.g., 'doctorChange')
   */
  const navigateToDate = useCallback((selectedDate, view) => {
    const startDate = moment(selectedDate).format('Y-MM-01');

    // Update local state
    setCurrentDate(startDate);

    // Update URL
    const newUrl = buildCalendarUrl(match.url, startDate, user?.UdaDrList?.abbr || '');
    dispatch(push(newUrl));

    // Don't fetch if no doctor selected
    if (user?.UdaDrList?.abbr === null) return;

    // Only fetch data if date changed and is current or past month, or if doctor changed
    const shouldFetch =
      (currentDate !== startDate && isCurrentOrPastMonth(startDate)) ||
      view === 'doctorChange';

    if (shouldFetch && isCurrentOrPastMonth(startDate)) {
      const { endDate } = getMonthRange(selectedDate);

      const params = {
        startDate: startDate,
        endDate: endDate,
        source: 'DrPay',
        apiType2: 'calendar',
        abbr: user.UdaDrList.abbr,
      };

      // Call the callback to fetch data
      if (onNavigateCallback) {
        onNavigateCallback(params);
      }
    }
  }, [currentDate, match.url, user, dispatch, onNavigateCallback]);

  /**
   * Handle month change from calendar component
   * @param {Date} date - Selected date from calendar
   * @param {string} view - View type
   */
  const handleMonthChange = useCallback((date, view) => {
    navigateToDate(date, view);
  }, [navigateToDate]);

  return {
    currentDate,
    navigateToDate,
    handleMonthChange,
    setCurrentDate
  };
};
